import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import haukeApiService from '../services/haukeApi';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import '../assets/css/user-suggestions.css';

const UserSuggestions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // Status Filter
  const tabs = [
    { id: 'PENDING', label: 'Ausstehend', icon: 'schedule' },
    { id: 'APPROVED', label: 'Genehmigt', icon: 'check_circle' },
    { id: 'REJECTED', label: 'Abgelehnt', icon: 'cancel' },
    { id: 'all', label: 'Alle', icon: 'view_list' }
  ];
  const [activeTab, setActiveTab] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, hasNext: false, hasPrev: false });
  // Geschlossene Meldungen (user_declined)
  const [declined, setDeclined] = useState([]);
  const [declinedLoading, setDeclinedLoading] = useState(false);
  const [declinedError, setDeclinedError] = useState(null);
  const [declinedPage, setDeclinedPage] = useState(1);
  const [declinedLimit, setDeclinedLimit] = useState(10);
  const [declinedPagination, setDeclinedPagination] = useState({ page: 1, limit: 10, hasNext: false, hasPrev: false });

  useEffect(() => {
    fetchSuggestions();
  }, [activeTab, search, dateFrom, dateTo, page, limit]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchDeclined();
  }, [search, dateFrom, dateTo, declinedPage, declinedLimit]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        status: activeTab || 'PENDING',
        search: search || '',
        dateFrom: dateFrom || '',
        dateTo: dateTo || '',
        limit,
        page
      };
      const data = await haukeApiService.getUserSuggestions(params);
      setSuggestions(data.data || []);
      setPagination(data.pagination || { page, limit, hasNext: (data.data || []).length === limit, hasPrev: page > 1 });
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setError(`Fehler beim Laden: ${error.message}`);
      showErrorToast('Fehler beim Laden der User Suggestions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeclined = async () => {
    try {
      setDeclinedLoading(true);
      setDeclinedError(null);
      const params = {
        search: search || '',
        dateFrom: dateFrom || '',
        dateTo: dateTo || '',
        limit: declinedLimit,
        page: declinedPage
      };
      const data = await haukeApiService.getUserDeclined(params);
      setDeclined(data.data || []);
      setDeclinedPagination(data.pagination || { page: declinedPage, limit: declinedLimit, hasNext: (data.data || []).length === declinedLimit, hasPrev: declinedPage > 1 });
    } catch (e) {
      console.error('Error loading closed reports:', e);
      setDeclinedError(e?.message || 'Fehler beim Laden');
    } finally {
      setDeclinedLoading(false);
    }
  };

  const closeDeclined = async (item) => {
    try {
      await haukeApiService.updateUserDeclined(item.id, { status: 'CLOSED' });
      showSuccessToast('Als bearbeitet markiert');
      setDeclined(prev => prev.map(d => d.id === item.id ? { ...d, _closed: true } : d));
    } catch (e) {
      console.error('Error closing report:', e);
      showErrorToast('Markieren fehlgeschlagen');
    }
  };

  const fetchStats = async () => {
    try {
      const data = await haukeApiService.getUserSuggestionsStats();
      const s = data?.data || {};
      setStats({
        total: s.total || 0,
        pending: s.pending || 0,
        approved: s.approved || 0,
        rejected: s.rejected || 0
      });
    } catch (e) {
      // optional
    }
  };

  const updateSuggestionStatus = async (suggestionId, newStatus, adminNotes = '') => {
    try {
      const response = await fetch(`https://europe-west3-lf1-app.cloudfunctions.net/userSuggestions/${suggestionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          adminNotes: adminNotes,
          processedAt: new Date().toISOString(),
          processedBy: user?.email || 'Unknown'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Update local state
        setSuggestions(prev => prev.map(s => 
          s.id === suggestionId 
            ? { ...s, status: newStatus, adminNotes, processedAt: new Date().toISOString() }
            : s
        ));
        showSuccessToast(`Status erfolgreich auf "${getStatusLabel(newStatus)}" geändert`);
      }
    } catch (error) {
      console.error('Error updating suggestion:', error);
      showErrorToast('Fehler beim Aktualisieren des Status');
    }
  };



  const getStatusLabel = (status) => {
    const statusMap = {
      'PENDING': 'Ausstehend',
      'APPROVED': 'Genehmigt',
      'REJECTED': 'Abgelehnt'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING': '#f59e0b',
      'APPROVED': '#10b981',
      'REJECTED': '#ef4444'
    };
    return colorMap[status] || '#6b7280';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRowDoubleClick = (suggestion) => {
    navigate(`/user-suggestions/${suggestion.id}`);
  };

  // Serverseitig gefiltert
  const filteredSuggestions = suggestions;

  return (
    <div className="direkterzeuger-admin">
      <div className="admin-header">
        <div className="header-content">
              <div className="header-left">
                <h1>User Suggestions</h1>
                <p className="header-description">Verwaltung aller User Suggestions aus der App</p>
                <div className="tasks-tabs" style={{ marginTop: '12px' }}>
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => { setActiveTab(tab.id); setPage(1); }}
                    >
                      <i className="material-icons">{tab.icon}</i>
                      {tab.label}
                      <span className="tab-badge">
                        {tab.id === 'PENDING' && stats.pending}
                        {tab.id === 'APPROVED' && stats.approved}
                        {tab.id === 'REJECTED' && stats.rejected}
                        {tab.id === 'all' && stats.total}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
          <div className="header-right">
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{suggestions.length}</span>
                <span className="stat-label">Gefunden</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="search-section">
        <div className="search-form">
          <div className="form-row">
            <div className="form-group">
              <label>Suche</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name, Ort, PLZ, Straße oder Benutzer"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <div className="form-group">
              <label>Von</label>
              <input type="date" className="form-control" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            </div>
            <div className="form-group">
              <label>Bis</label>
              <input type="date" className="form-control" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={fetchSuggestions} disabled={loading}>
                <i className="material-icons">refresh</i>
                Aktualisieren
              </button>
            </div>
            <div className="form-group" style={{ marginLeft: 'auto' }}>
              <label>Pro Seite</label>
              <select className="form-control" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="results-section">
        {loading && (
          <div className="loading-state">
            <i className="material-icons">hourglass_empty</i>
            <p>Lade User Suggestions...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <i className="material-icons">error</i>
            <p>{error}</p>
            <button onClick={fetchSuggestions} className="btn btn-primary">
              Erneut versuchen
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name/Ort</th>
                  <th>Benutzer</th>
                  <th>Features</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuggestions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">
                      <i className="material-icons">inbox</i>
                      <p>Keine User Suggestions gefunden</p>
                      <small>Versuchen Sie andere Filtereinstellungen</small>
                    </td>
                  </tr>
                ) : (
                  filteredSuggestions.map((suggestion) => (
                    <tr 
                      key={suggestion.id} 
                      onDoubleClick={() => handleRowDoubleClick(suggestion)}
                      title="Doppelklick für Details"
                    >
                      <td>
                        <div className="suggestion-info">
                          <div className="suggestion-name">
                            <strong>{suggestion.name || 'Unbekannt'}</strong>
                          </div>
                          <div className="suggestion-location">
                            {suggestion.street && `${suggestion.street}, `}
                            {suggestion.plz} {suggestion.city}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="user-info">
                          <strong>{suggestion.userName || 'Unbekannt'}</strong>
                          <small>{suggestion.userId}</small>
                        </div>
                      </td>
                      <td>
                        <div className="features-list">
                          {suggestion.features && suggestion.features.length > 0 ? (
                            <span className="feature-count">
                              {suggestion.features.length} Features
                            </span>
                          ) : (
                            <span className="no-features">Keine Features</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(suggestion.status) }}
                        >
                          {getStatusLabel(suggestion.status)}
                        </span>
                      </td>
                      <td>{formatDate(suggestion.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          {suggestion.status === 'PENDING' && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateSuggestionStatus(suggestion.id, 'APPROVED');
                                }}
                                title="Genehmigen"
                              >
                                <i className="material-icons">check</i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateSuggestionStatus(suggestion.id, 'REJECTED');
                                }}
                                title="Ablehnen"
                              >
                                <i className="material-icons">close</i>
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/user-suggestions/${suggestion.id}`);
                            }}
                            title="Details anzeigen"
                          >
                            <i className="material-icons">visibility</i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="pagination-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <div>
                Seite {pagination.page} {pagination.hasNext || pagination.hasPrev ? '' : ''}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-outline" disabled={!pagination.hasPrev || loading} onClick={() => setPage(Math.max(1, page - 1))}>
                  <i className="material-icons">chevron_left</i>
                  Zurück
                </button>
                <button className="btn btn-outline" disabled={!pagination.hasNext || loading} onClick={() => setPage(page + 1)}>
                  Weiter
                  <i className="material-icons">chevron_right</i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Geschlossene Meldungen */}
      <div className="results-section" style={{ marginTop: '24px' }}>
        <div className="admin-header" style={{ padding: 0, marginBottom: '12px' }}>
          <div className="header-content">
            <div className="header-left">
              <h2>Geschlossene Meldungen</h2>
              <p className="header-description">Vorschläge, die als "geschlossen/abgelehnt" erfasst wurden</p>
            </div>
            <div className="header-right">
              <div className="header-stats">
                <div className="stat-item">
                  <span className="stat-number">{declined.length}</span>
                  <span className="stat-label">Gefunden</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {declinedLoading && (
          <div className="loading-state">
            <i className="material-icons">hourglass_empty</i>
            <p>Lade geschlossene Meldungen...</p>
          </div>
        )}

        {declinedError && (
          <div className="error-state">
            <i className="material-icons">error</i>
            <p>{declinedError}</p>
            <button onClick={fetchDeclined} className="btn btn-primary">Erneut versuchen</button>
          </div>
        )}

        {!declinedLoading && !declinedError && (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name/Ort</th>
                  <th>Benutzer</th>
                  <th>Hof-ID</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {declined.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data">
                      <i className="material-icons">inbox</i>
                      <p>Keine geschlossenen Meldungen gefunden</p>
                    </td>
                  </tr>
                ) : (
                  declined.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="suggestion-info">
                          <div className="suggestion-name">
                            <strong>{item.name || 'Unbekannt'}</strong>
                          </div>
                          <div className="suggestion-location">
                            {item.plz ? `${item.plz} ` : ''}{item.city || ''}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="user-info">
                          <strong>{item.userName || 'Unbekannt'}</strong>
                          <small>{item.userId}</small>
                        </div>
                      </td>
                      <td>
                        <code>{item.hofId || '-'}</code>
                      </td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => closeDeclined(item)}
                            title="Als bearbeitet markieren"
                          >
                            <i className="material-icons">check</i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="pagination-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <div>
                Seite {declinedPagination.page}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="form-group" style={{ marginRight: '12px' }}>
                  <label>Pro Seite</label>
                  <select className="form-control" value={declinedLimit} onChange={(e) => { setDeclinedLimit(Number(e.target.value)); setDeclinedPage(1); }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <button className="btn btn-outline" disabled={!declinedPagination.hasPrev || declinedLoading} onClick={() => setDeclinedPage(Math.max(1, declinedPage - 1))}>
                  <i className="material-icons">chevron_left</i>
                  Zurück
                </button>
                <button className="btn btn-outline" disabled={!declinedPagination.hasNext || declinedLoading} onClick={() => setDeclinedPage(declinedPage + 1)}>
                  Weiter
                  <i className="material-icons">chevron_right</i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default UserSuggestions;